/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';
import { translations } from '@/i18n/translations';

interface TranslationChapter {
    keys: string[];
    title: string;
}

function getTranslationChapters(): TranslationChapter[] {
    const chapters: TranslationChapter[] = [];
    let currentChapter: TranslationChapter | null = null;

    Object.entries(translations.de).forEach(([key, value]) => {
        if (key.startsWith('#chapter-')) {
            currentChapter = { keys: [], title: value };
            chapters.push(currentChapter);
            return;
        }

        currentChapter?.keys.push(key);
    });

    return chapters;
}

const TRANSLATION_CHAPTERS = getTranslationChapters();

function getTranslation(language: keyof typeof translations, key: string): string {
    return translations[language][key] ?? key;
}

function TranslationsTab() {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-2">
                <h2>{t('Translations')}</h2>
                <p>{t('Browse all project translation keys and their German and Spanish values.')}</p>
            </section>

            {TRANSLATION_CHAPTERS.map((chapter, chapterIndex) => {
                const firstRowNumber = TRANSLATION_CHAPTERS
                    .slice(0, chapterIndex)
                    .reduce((total, previousChapter) => total + previousChapter.keys.length, 1);

                return (
                    <section key={chapter.title} className="flex flex-col gap-3">
                        <h3>{chapter.title}</h3>
                        <div className="overflow-x-auto rounded-box border border-base-content/20">
                            <table className="table table-sm w-full">
                                <thead className="bg-base-content/10 text-sm text-base-content/80">
                                    <tr>
                                        <th className="w-16 px-4 py-3">#</th>
                                        <th className="min-w-72 px-4 py-3">{t('Key / English')}</th>
                                        <th className="min-w-64 px-4 py-3">{t('German')}</th>
                                        <th className="min-w-64 px-4 py-3">{t('Spanish')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-base-300">
                                    {chapter.keys.map((key, index) => (
                                        <tr key={key} className="even:bg-base-content/5 hover:bg-base-content/10">
                                            <td className="align-top px-4 py-3 text-base-content/70">
                                                {firstRowNumber + index}.
                                            </td>
                                            <td className="align-top px-4 py-3">
                                                <span className="break-words">
                                                    {getTranslation('en', key)}
                                                </span>
                                            </td>
                                            <td className="align-top px-4 py-3 whitespace-pre-wrap">
                                                {getTranslation('de', key)}
                                            </td>
                                            <td className="align-top px-4 py-3 whitespace-pre-wrap">
                                                {getTranslation('es', key)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                );
            })}
        </div>
    );
}

export default TranslationsTab;
